'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { Home, Activity } from 'lucide-react';

interface Block {
    width: number;
    height: number;
    top: string;
    left: string;
    duration: number;
    delay: number;
}

interface Particle {
    width: number;
    height: number;
    top: string;
    left: string;
    duration: number;
}

export default function NotFound() {
    const [blocks, setBlocks] = useState<Block[]>([]);
    const [particles, setParticles] = useState<Particle[]>([]);

    useEffect(() => {
        // Generate random animation data on the client only
        setBlocks(
            Array.from({ length: 20 }, () => ({
                width: Math.random() * 30 + 10,
                height: Math.random() * 30 + 10,
                top: Math.random() * 100 + '%',
                left: Math.random() * 100 + '%',
                duration: Math.random() * 10 + 10,
                delay: Math.random() * 5,
            }))
        );

        setParticles(
            Array.from({ length: 25 }, () => ({
                width: Math.random() * 2 + 1,
                height: Math.random() * 2 + 1,
                top: Math.random() * 100 + '%',
                left: Math.random() * 100 + '%',
                duration: Math.random() * 2 + 2,
            }))
        );
    }, []);

    return (
        <div className="relative flex flex-col items-center justify-center min-h-screen w-full overflow-hidden bg-gradient-to-b from-[#0a0a0a] via-[#101010] to-[#0a0a0a] text-center text-white">
            {/* Moving pixel blocks (ambient background) */}
            <div className="absolute inset-0 overflow-hidden opacity-20 pointer-events-none">
                {blocks.map((b, i) => (
                    <motion.div
                        key={i}
                        className="absolute bg-green-500/20"
                        style={{
                            width: b.width,
                            height: b.height,
                            top: b.top,
                            left: b.left,
                        }}
                        animate={{
                            y: ['0%', '120%'],
                            rotate: [0, 90, 180],
                        }}
                        transition={{
                            duration: b.duration,
                            repeat: Infinity,
                            delay: b.delay,
                            ease: 'linear',
                        }}
                    />
                ))}
            </div>

            {/* Torch glow light effect */}
            <motion.div
                className="absolute bottom-[20%] left-1/2 -translate-x-1/2 w-80 h-80 bg-amber-500/20 blur-3xl rounded-full pointer-events-none"
                animate={{
                    opacity: [0.2, 0.5, 0.3, 0.6],
                    scale: [1, 1.1, 0.9, 1],
                }}
                transition={{
                    duration: 1.6,
                    repeat: Infinity,
                }}
            />

            {/* 404 Code */}
            <motion.h1
                className="font-extrabold text-[5rem] md:text-[7rem] bg-gradient-to-b from-green-400 via-lime-300 to-green-600 bg-clip-text text-transparent tracking-tight drop-shadow-[0_0_20px_rgba(34,197,94,0.4)]"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.8 }}
            >
                404
            </motion.h1>

            {/* Subtitle */}
            <motion.p
                className="text-gray-300 text-base md:text-lg max-w-xl mx-auto mt-3 font-mono leading-relaxed"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
            >
                <span className="text-emerald-400">Steve</span> tried to mine this page...
                but the chunk failed to load.
            </motion.p>

            {/* Divider */}
            <motion.div
                className="w-24 h-1 bg-gradient-to-r from-green-500 via-lime-400 to-green-500 rounded-full mt-6"
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: '6rem', opacity: 1 }}
                transition={{ delay: 0.6, duration: 0.6 }}
            />

            {/* Action Buttons */}
            <motion.div
                className="mt-10 flex flex-col sm:flex-row gap-4 z-10"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
            >
                <Link
                    href="/"
                    className="bg-gradient-to-br from-green-500 to-lime-500 text-black font-semibold px-6 py-3 rounded-lg shadow-lg hover:scale-105 transition-transform inline-flex items-center justify-center gap-2"
                >
                    <Home className="w-4 h-4" />
                    <span>Back to Home</span>
                </Link>

                <Link
                    href="/spark-analyzer"
                    className="bg-gradient-to-br from-emerald-700 to-emerald-500 text-white font-semibold px-6 py-3 rounded-lg shadow-lg hover:scale-105 transition-transform inline-flex items-center justify-center gap-2"
                >
                    <Activity className="w-4 h-4" />
                    <span>Try Spark Analyzer</span>
                </Link>
            </motion.div>

            {/* Floating Dust Particles */}
            <div className="absolute inset-0 overflow-hidden z-0 pointer-events-none">
                {particles.map((p, i) => (
                    <motion.span
                        key={i}
                        className="absolute bg-white/10 rounded-full"
                        style={{
                            width: p.width,
                            height: p.height,
                            top: p.top,
                            left: p.left,
                        }}
                        animate={{
                            y: [0, -10],
                            opacity: [0.1, 0.3, 0.1],
                        }}
                        transition={{
                            duration: p.duration,
                            repeat: Infinity,
                            repeatType: 'mirror',
                        }}
                    />
                ))}
            </div>
        </div>
    );
}
